import CTAGroup from "@/components/CTAGroup";
import FAQ from "@/components/FAQ";
import FinalCTA from "@/components/FinalCTA";
import ChatWidget from "@/components/ChatWidget";
import StoryCards from "@/components/StoryCards";
import StickyAssessmentBar from "@/components/StickyAssessmentBar";
import { INCLUDED, FSA_EXAMPLES, EXISTING_PLAN } from "@/lib/ignitePlan";
import {
    IGNITE_CONSULT_URL,
    PAPERFORM_URL,
    SHORT_FORM_VIDEO_URL,
    PLATFORM_DEMO_VIDEO_URL,
} from "@/lib/leads";
import {
    CheckCircle2,
    ArrowRight,
    CreditCard,
    ScrollText,
    FileCheck2,
    BadgeCheck,
    DollarSign,
    Ban,
} from "lucide-react";

const TRUST = [
    {
        icon: DollarSign,
        t: "$620+/employee/year",
        b: "Average employer savings on FICA, FUTA, and SUTA — funded through the plan structure, not the P&L.",
    },
    {
        icon: ScrollText,
        t: "IRS Sections 105, 106, 213(d), 125",
        b: "Full plan documentation provided under the code sections that govern medical reimbursement and cafeteria plans.",
    },
    {
        icon: Ban,
        t: 'No "double dip"',
        b: "Reimbursements are backed by bona fide Section 213(d) services and actuarial fair market value — excluded from gross income under IRS 105(b).",
    },
    {
        icon: FileCheck2,
        t: "Free ACA reporting",
        b: "Form 1094 and 1095 reporting included at no cost — Part A requirements met affordably.",
    },
    {
        icon: BadgeCheck,
        t: "Audit ready",
        b: "Proper documentation, notices, and substantiation are in place from day one — full audit readiness.",
    },
];

const PROCESS = [
    { n: "01", t: "Share basics", b: "A few quick details about your organization." },
    { n: "02", t: "We've already done the legwork", b: "Every employer we reach out to has been researched for fit before we ever connect." },
    { n: "03", t: "Meet Ignite Health", b: "You get an intro email and a direct link to schedule with Giovanni, right away." },
];

const INFO_REQUESTED = [
    "Your name and role",
    "Company name and state",
    "Approximate W2 team size",
    "Current payroll processor (if you have one)",
    "Business email",
];

const FAQS = [
    {
        q: "Is this a sales call?",
        a: "No. This connects you directly with a dedicated Ignite Health representative — an advisory conversation with someone who knows the plan, not a sales pitch. Submitting the form doesn't commit you to anything; you'll get an intro email and a link to talk whenever works for you.",
    },
    {
        q: "What if I'm still exploring?",
        a: "That's fine — watching the overview or submitting the form doesn't commit you to anything. You'll get the intro email either way, and you can decide from there whether to book a call.",
    },
    {
        q: "What size company is this best for?",
        a: "Ignite Health works well for small and midsize businesses. Share your approximate team size on the form and Ignite Health will confirm fit for your organization.",
    },
    {
        q: "Do I need payroll details to start?",
        a: "No. The form just asks for a few basics — your name and role, company name and state, approximate team size, and your current payroll processor if you have one. No payroll numbers or financial data required to get started.",
    },
    {
        q: "Do employees have to sign up?",
        a: "No. Once an employer implements the plan, eligible employees are automatically enrolled — no individual signup or action required on their part.",
    },
    {
        q: "How long does implementation take?",
        a: "About two weeks from start to finish once you move forward — a straightforward setup with minimal disruption to your team.",
    },
    {
        q: "What does this cost?",
        a: "Nothing for employees — the plan carries no out-of-pocket cost and doesn't reduce take-home pay. For employers, there's a small program fee, typically more than offset by payroll tax savings — most employers come out ahead, not behind.",
    },
    {
        q: "Can I book directly with Ignite Health?",
        a: "Yes. If you already know you want to move forward, there's a direct scheduling link on this page — no form required first.",
    },
];

export default function IgniteHealth() {
    return (
        <div data-testid="page-ignite">
            <section className="relative section pt-16 md:pt-24 overflow-hidden" data-testid="ignite-hero">
                <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 space-y-7">
                        <p className="eyebrow">Featured partner campaign</p>
                        <h1 className="h-display">
                            The HR Connect <span className="text-navy">×</span> Ignite Health
                        </h1>
                        <p className="font-heading text-2xl md:text-3xl text-ink-soft">
                            Get a free custom benefits proposal.
                        </p>
                        <p className="lead max-w-2xl">
                            Ignite Health gives eligible employers access to
                            telemedicine, mental health support, prescription
                            drug coverage, preventive care, biomarker testing,
                            and an FSA card — at no out-of-pocket cost to
                            employees. HR Connect evaluates fit and makes the
                            introduction; Ignite Health handles everything from
                            there.
                        </p>
                        <p className="text-sm text-ink-soft max-w-2xl">
                            Built around IRS Sections 105, 106, 213(d), and
                            125, with free ACA compliance reporting included.
                        </p>
                        <CTAGroup
                            testIdPrefix="ignite-hero-cta"
                            primaryLabel="Watch the 2-Minute Overview ↓"
                            primaryHref="#video"
                        />
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden border border-border">
                            <img
                                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3ODExOTkwNjN8MA&ixlib=rb-4.1.0&q=85"
                                alt="Healthcare advisor reviewing benefits"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* PRIMARY video section — required viewing */}
            <section
                id="video"
                className="section bg-surface border-t border-border"
                data-testid="ignite-video"
            >
                <div className="container-x max-w-4xl">
                    <div className="max-w-2xl mb-10">
                        <p className="eyebrow mb-4">Overview</p>
                        <h2 className="h2">See how it works in 2 minutes.</h2>
                        <p className="lead mt-5">
                            A quick look at what Ignite Health offers and whether
                            it's a fit for your team.
                        </p>
                    </div>

                    <div
                        className="aspect-video w-full border border-border bg-black overflow-hidden mb-10"
                        data-testid="ignite-video-main"
                    >
                        <video
                            className="w-full h-full"
                            controls
                            playsInline
                            preload="metadata"
                            data-testid="video-primary-player"
                        >
                            <source src={SHORT_FORM_VIDEO_URL} type="video/mp4" />
                            Your browser doesn't support embedded video.
                        </video>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                        <a
                            href={PAPERFORM_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="video-primary-cta"
                            className="group inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow"
                        >
                            Get Your Free Benefits Assessment
                            <ArrowRight
                                className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                                strokeWidth={1.75}
                            />
                        </a>
                        <a
                            href={IGNITE_CONSULT_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="video-supporting"
                            className="text-sm text-ink-soft hover:text-brand transition-colors"
                        >
                            Already know what you need?{" "}
                            <span className="underline underline-offset-4 decoration-border">
                                Book a consult directly with Ignite Health
                            </span>{" "}
                            →
                        </a>
                    </div>
                </div>
            </section>

            {/* WHAT'S INCLUDED — 6 core benefits + supplemental FSA callout */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="ignite-included"
            >
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">What's included</p>
                        <h2 className="h2">
                            A full benefits stack —{" "}
                            <span className="text-navy">no out-of-pocket cost to employees.</span>
                        </h2>
                        <p className="lead mt-5">
                            Every eligible employee is automatically enrolled and
                            gets access to the following, day one.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                        {INCLUDED.map((b) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={b.t}
                                    className="bg-background p-8 space-y-4"
                                    data-testid={`included-${b.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon className="h-6 w-6 text-brand" strokeWidth={1.5} />
                                    <h3 className="font-heading text-xl">{b.t}</h3>
                                    <p className="body text-sm">{b.b}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* FSA + supplemental card (spans full width) */}
                    <div
                        className="mt-6 border border-border bg-background p-8 md:p-10 grid lg:grid-cols-12 gap-8"
                        data-testid="included-supplemental"
                    >
                        <div className="lg:col-span-5 space-y-4">
                            <CreditCard className="h-6 w-6 text-brand" strokeWidth={1.5} />
                            <h3 className="font-heading text-xl">
                                Supplemental Benefits — Dental + FSA Card
                            </h3>
                            <p className="body text-sm">
                                Extra savings go toward a dental plan and a
                                prepaid FSA card employees can use on eligible
                                healthcare purchases. It's real spending power,
                                not a credit line.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4 text-ink-soft/80">
                                What employees can buy with the FSA card
                            </p>
                            <ul className="flex flex-wrap gap-2">
                                {FSA_EXAMPLES.map((item) => (
                                    <li
                                        key={item}
                                        data-testid={`fsa-example-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="inline-flex items-center border border-border px-3 py-1.5 text-xs text-ink-soft bg-surface"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* WORKS ALONGSIDE YOUR EXISTING PLAN */}
            <section
                className="section border-t border-border"
                data-testid="ignite-existing-plan"
            >
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">
                            Works alongside your existing plan
                        </p>
                        <h2 className="h2">
                            Same coverage.{" "}
                            <span className="text-navy">More support. Greater value.</span>
                        </h2>
                        <p className="lead mt-5">
                            Ignite Health isn't a replacement for your current
                            medical plan — it strengthens it.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {EXISTING_PLAN.map((e) => {
                            const Icon = e.icon;
                            return (
                                <div
                                    key={e.t}
                                    className="space-y-4"
                                    data-testid={`existing-plan-${e.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon
                                        className="h-6 w-6 text-brand"
                                        strokeWidth={1.5}
                                    />
                                    <h3 className="font-heading text-xl">{e.t}</h3>
                                    <p className="body text-sm">{e.b}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TRUST & COMPLIANCE STRIP */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="ignite-trust"
            >
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">Trust &amp; compliance</p>
                        <h2 className="h2">
                            Compliant by design —{" "}
                            <span className="text-navy">not by shortcut.</span>
                        </h2>
                        <p className="lead mt-5">
                            The Ignite Plan is built around federal tax code and
                            actively managed every payroll period.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-border">
                        {TRUST.map((t) => {
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.t}
                                    className="bg-background p-6 space-y-3"
                                    data-testid={`trust-${t.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon
                                        className="h-5 w-5 text-brand"
                                        strokeWidth={1.5}
                                    />
                                    <p className="font-heading text-base leading-snug">
                                        {t.t}
                                    </p>
                                    <p className="text-xs text-ink-soft leading-relaxed">
                                        {t.b}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="section" data-testid="ignite-process">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-4">How the process works</p>
                        <h2 className="h2">Three steps. No surprises.</h2>
                        <p className="body mt-5 max-w-md">
                            The HR Connect handles intake and review. Ignite Health
                            handles benefits solutions. You stay in control of the pace.
                        </p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 space-y-6">
                        {PROCESS.map((p) => (
                            <div
                                key={p.n}
                                className="border-l-2 border-brand pl-6 py-2"
                                data-testid={`ignite-process-${p.n}`}
                            >
                                <p className="font-heading text-3xl text-navy">{p.n}</p>
                                <h3 className="font-heading text-xl mt-1">{p.t}</h3>
                                <p className="text-sm text-ink-soft mt-1">{p.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section bg-surface" data-testid="ignite-info">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-4">What information may be requested</p>
                        <h2 className="h2">A small amount of context, used well.</h2>
                        <p className="body mt-5 max-w-md">
                            Sharing these details helps The HR Connect prepare a more
                            relevant proposal — and avoids wasting your time on
                            generic options.
                        </p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <ul className="divide-y divide-border border-y border-border">
                            {INFO_REQUESTED.map((item) => (
                                <li
                                    key={item}
                                    className="py-5 flex items-start gap-3 body"
                                    data-testid={`ignite-info-${item.split(" ")[0].toLowerCase()}`}
                                >
                                    <CheckCircle2
                                        className="h-5 w-5 text-navy shrink-0 mt-1"
                                        strokeWidth={1.5}
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="section" data-testid="ignite-after">
                <div className="container-x max-w-4xl">
                    <p className="eyebrow mb-4">What happens after you submit</p>
                    <h2 className="h2 mb-8">Straight to the intro — no waiting on a review.</h2>
                    <div className="space-y-5 body">
                        <p>
                            As soon as you submit, you'll get an intro email
                            connecting you directly with Ignite Health — along
                            with a link to schedule a call whenever works for
                            you. No waiting on a review; the fit-check already
                            happened before we reached out to you.
                        </p>
                        <p>
                            If you're already ready,{" "}
                            <a
                                href={IGNITE_CONSULT_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="ignite-after-direct-link"
                                className="text-foreground hover:text-brand underline underline-offset-4 decoration-border"
                            >
                                you can book directly with Ignite Health
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </section>

            <section className="section bg-surface" data-testid="ignite-stories">
                <div className="container-x">
                    <div className="max-w-3xl mb-12">
                        <p className="eyebrow mb-4">Stories from employers</p>
                        <h2 className="h2">
                            Real teams. Real results with the{" "}
                            <span className="text-navy">Ignite Plan.</span>
                        </h2>
                        <p className="lead mt-5">
                            Anonymized outcomes from employers across healthcare,
                            hospitality, manufacturing, education, and more —
                            spanning retention, morale, and healthcare spend.
                        </p>
                    </div>
                    <StoryCards limit={6} testIdPrefix="ignite-story" />
                </div>
            </section>

            {/* SECONDARY video block — optional platform demo (moved here per spec) */}
            <section
                className="pb-16 md:pb-20 pt-4"
                data-testid="ignite-video-secondary"
            >
                <div className="container-x max-w-3xl">
                    <div className="mb-6">
                        <p className="eyebrow mb-2 text-ink-soft/80">Optional</p>
                        <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy">
                            Want to see the platform itself?
                        </h3>
                        <p className="body text-sm mt-3 max-w-lg">
                            A short look inside Ignite Health for teams that
                            already like the concept.
                        </p>
                    </div>
                    <div
                        className="aspect-video w-full border border-border bg-black overflow-hidden"
                        data-testid="ignite-video-secondary-player-wrap"
                    >
                        <video
                            className="w-full h-full"
                            controls
                            playsInline
                            preload="metadata"
                            data-testid="video-demo-player"
                        >
                            <source src={PLATFORM_DEMO_VIDEO_URL} type="video/mp4" />
                            Your browser doesn't support embedded video.
                        </video>
                    </div>
                </div>
            </section>

            <section className="section" data-testid="ignite-faq">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <p className="eyebrow mb-4">FAQ</p>
                        <h2 className="h2">Good questions, answered.</h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <FAQ items={FAQS} testIdPrefix="ignite-faq" />
                    </div>
                </div>
            </section>

            <FinalCTA
                eyebrow="Ready when you are"
                heading="Start the HR Connect × Ignite Health path."
                body="Get your free benefits assessment, or book directly with Ignite Health if you're ready now."
                testIdPrefix="ignite-final"
            />
            <ChatWidget />
            <StickyAssessmentBar />
        </div>
    );
}
